import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash-es';
import styles from './FloAccessNag.module.css';
import {
	STARTER_ACCESS_EXPIRED_TITLE,
	STARTER_ACCESS_EXPIRED_BODY,
	STARTER_ACCESS_EXPIRED_CTA_TEXT,
	STARTER_ACCESS_EXPIRED_DISMISS_TEXT,
	STARTER_NAG_REAPPEAR_MS,
} from '../../Common/Common.constants';
import { WEBSITE_PRICING_PAGE } from '../../Common/Common.env';
import { getFloDetails } from '../../Pages/Flo/Flo.reducers';

const LockIcon = () => (
	<svg
		className={styles.lockIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
		<path d="M7 11V7a5 5 0 0 1 10 0v4" />
	</svg>
);

/**
 * Gate for published Flo content when the owning flospace's Starter grace
 * period has expired. While locked the children (the actual Flo output) are
 * NOT mounted, so hiding this element via devtools reveals nothing. "View
 * anyway" dismisses the lock and triggers a real content fetch (the initial
 * expired load skips the content request entirely) - the lock then re-appears
 * after STARTER_NAG_REAPPEAR_MS to nudge toward upgrading.
 */
const FloAccessNag = ({
	floId,
	floIndex,
	children,
}: {
	floId?: string | null;
	floIndex: number;
	children?: ReactNode;
}) => {
	const dispatch = useDispatch();
	const accessState = useSelector((state) =>
		get(state, 'floPage.floSpace.subscriptionAccess.accessState', '')
	);
	const floSpaceId = useSelector((state) =>
		get(state, 'floPage.floSpace.flospaceId', '')
	);
	// floSpace.flospaceId is only populated once getFloSpaceData ran; fall back to
	// the bootstrap script's data-flospace-id so the saga's security check passes.
	const resolvedFloSpaceId =
		floSpaceId || document.getElementById('FLOIK_JSON')?.dataset?.flospaceId || '';
	// floDetails is only populated once content has actually been fetched; on
	// the expired path the initial getFloDetails returns before fetching, so we
	// re-dispatch with skipAccessCheck on the first "View anyway" click.
	const floLoaded = useSelector((state) =>
		Boolean(get(state, `floPage.flos.${floIndex}.floDetails.floId`))
	);
	// flo_{index} loader stays >0 until the saga finishes fetching f_cfg.json +
	// media + tracks - floDetails lands mid-load, so gate the countdown on both.
	const floLoading = useSelector((state) => get(state, `loader.flo_${floIndex}`, 0) > 0);
	const isExpired = accessState === 'EXPIRED';
	const isGrace = accessState === 'GRACE';
	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		if (!dismissed) return;
		// Don't start the re-lock countdown until the flo has actually finished
		// loading (data + loader cleared) - a slow fetch shouldn't eat the
		// preview window. The fallback covers a failed fetch so we never stay
		// unlocked forever.
		if (!floLoaded || floLoading) {
			const fallback = setTimeout(() => setDismissed(false), 30000);
			return () => clearTimeout(fallback);
		}
		// const timer = setTimeout(() => setDismissed(false), STARTER_NAG_REAPPEAR_MS);
		// return () => clearTimeout(timer);
	}, [isExpired, dismissed, floLoaded, floLoading]);

	// While locked the content is unmounted - pause any still-playing media.
	// Detached `new Audio()` instances (bg music / narration) also pause via
	// their own unmount cleanups; this covers DOM media elements.
	useEffect(() => {
		if ((!isExpired && !isGrace) || dismissed) return;
		document.querySelectorAll('audio, video').forEach((el) => {
			try {
				(el as HTMLMediaElement).pause();
			} catch {}
		});
	}, [isExpired, dismissed, isGrace]);

	const onDismiss = () => {
		setDismissed(true);
		// No floId guard - the saga resolves data-flo-id / data-folder-id from the
		// FLOIK_JSON bootstrap itself when the prop isn't set.
		if (!floLoaded) {
			dispatch(
				getFloDetails({
					floId,
					floIndex,
					floSpaceId: resolvedFloSpaceId,
					skipAccessCheck: true,
				})
			);
		}
	};

	if ((!isExpired && !isGrace) || dismissed) return <>{children}</>;

	return (
		<div className={styles.nagOverlay}>
			<div className={styles.nagCard}>
				<div className={styles.lockBadge}>
					<LockIcon />
				</div>
				<span className={styles.nagTitle}>{STARTER_ACCESS_EXPIRED_TITLE}</span>
				<span className={styles.nagBody}>{STARTER_ACCESS_EXPIRED_BODY}</span>
				<a
					className={styles.upgradeButton}
					href={WEBSITE_PRICING_PAGE}
					target="_blank"
					rel="noreferrer"
				>
					{STARTER_ACCESS_EXPIRED_CTA_TEXT}
				</a>
				{isGrace && (
					<button type="button" className={styles.dismissButton} onClick={onDismiss}>
						{STARTER_ACCESS_EXPIRED_DISMISS_TEXT}
					</button>
				)}
			</div>
		</div>
	);
};

export default FloAccessNag;
